"use client";;
import { cn } from "../lib/utils";
import { motion, useAnimation, useReducedMotion } from "motion/react";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import PropTypes from 'prop-types';

const BookOpenIcon = forwardRef((
 {
  onMouseEnter,
  onMouseLeave,
  className,
  size = 24,
  duration = 1,
  isAnimated = true,
  ...props
 },
 ref,
) => {
 const controls = useAnimation();
 const reduced = useReducedMotion();
 const isControlled = useRef(false);

 useImperativeHandle(ref, () => {
  isControlled.current = true;
  return {
   startAnimation: () =>
    reduced ? controls.start("normal") : controls.start("animate"),
   stopAnimation: () => controls.start("normal"),
  };
 });

 const handleEnter = useCallback((e) => {
  if (!isAnimated || reduced) return;
  if (!isControlled.current) controls.start("animate");
  else onMouseEnter?.(e);
 }, [controls, reduced, isAnimated, onMouseEnter]);

 const handleLeave = useCallback((e) => {
  if (!isControlled.current) controls.start("normal");
  else onMouseLeave?.(e);
 }, [controls, onMouseLeave]);

 const iconVariants = {
  normal: { scale: 1, rotate: 0 },
  animate: {
   scale: [1, 1.05, 0.97, 1],
   rotate: [0, -2, 2, 0],
   transition: { duration: 0.9 * duration, ease: "easeInOut" },
  },
 };

 const spineVariants = {
  normal: { pathLength: 1 },
  animate: {
   pathLength: [0, 1],
   transition: { duration: 0.6 * duration, ease: "easeInOut" },
  },
 };

 const pagesVariants = {
  normal: { scale: 1, opacity: 1 },
  animate: {
   scale: [1, 1.05, 0.98, 1],
   opacity: [0.9, 1, 1],
   transition: { duration: 0.8 * duration, ease: "easeInOut", delay: 0.2 },
  },
 };

 return (
  <motion.div
   className={cn("inline-flex items-center justify-center", className)}
   onMouseEnter={handleEnter}
   onMouseLeave={handleLeave}
   {...props}>
   <motion.svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    animate={controls}
    initial="normal"
    variants={iconVariants}>
    <motion.path d="M12 7v14" variants={spineVariants} initial="normal" animate={controls} />
    <motion.path
     d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"
     variants={pagesVariants}
     initial="normal"
     animate={controls} />
   </motion.svg>
  </motion.div>
 );
});

BookOpenIcon.displayName = "BookOpenIcon";

BookOpenIcon.propTypes = {
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  className: PropTypes.string,
  size: PropTypes.number,
  duration: PropTypes.number,
  isAnimated: PropTypes.bool,
};

export { BookOpenIcon };
